import { Directive, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { INDEX_CALC } from 'CalculationsApp/ts/constants/calc-search-types.constants';
import { DEFAULT_CALC_COLUMNS, EDIT_COLUMNS_INSTRUCTION_TEXT } from 'IndicesApp/ts/apps/indices-app/constants';
import { CalcSearchTypes } from 'CalculationsApp/ts/types/calc-search-types.type';
import { UserPreference } from 'AccountsApp/ts/interfaces/user-preference.interface';
import { DataColumnInfo } from 'Angular/features/components/modals/edit-data-columns-modal/interfaces/data-column-info.interface';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';
import { CalcInfo } from 'CalculationsApp/ts/services/calculations-service.interface';
import { IndicesRootStore } from 'IndicesApp/ts/apps/indices-app/state/interfaces/indices-root-store.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { CalculationsService } from 'CalculationsApp/ts/services/calculations.service';

import { EditDataColumnsModalComponent } from 'Angular/features/components/modals/edit-data-columns-modal/edit-data-columns-modal.component';
import equal from 'Angular/utils/equal/deep-equal';

@Directive()
export abstract class IndicesEditColumnsModalDirective {
    @ViewChild(EditDataColumnsModalComponent) editDataColumnsModalComponent!: EditDataColumnsModalComponent;

    storeSubscription!: Subscription;

    MAX_COLS: number = 7;

    instructionText: string = EDIT_COLUMNS_INSTRUCTION_TEXT;

    loading: boolean = false;

    modalOpen: boolean = false;

    modalColumns: DataColumnInfo[] = [];

    modalDefaultColumns: DataColumnInfo[] = DEFAULT_CALC_COLUMNS;

    preferenceCalcsBaseNames: string[] = [];

    preferenceName: string = '';

    searchType: CalcSearchTypes = INDEX_CALC;

    constructor(private accountsService: AccountsService, private calculationsService: CalculationsService) {}

    ngOnInit(): void {
        this.loading = true;
        this.accountsService.getUserPreferences().subscribe({
            next: (preference: UserPreference) => {
                // @ts-ignore
                this.updateState(preference[this.preferenceName]);
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            },
        });
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: IndicesRootStore): void => {
        const prevCalcs = this.preferenceCalcsBaseNames;
        const newCalcs = state.preferenceIndicesCalcs;
        if (!equal(prevCalcs, newCalcs)) {
            this.preferenceCalcsBaseNames = newCalcs;
            this.calculationsService.getCalcInfo(state.preferenceIndicesCalcs).subscribe((data) => {
                this.modalColumns = this.createDataColumns(data);
            });
        }
    };

    openModal(): void {
        this.editDataColumnsModalComponent.openModal();
    }

    submitModal(calcDataColumns: DataColumnInfo[]): void {
        const calcBaseNames = calcDataColumns.map((column) => column.metricInternalName).slice(0, this.MAX_COLS);
        // if new calcs are different than the one in state, update calc preferences
        if (!equal(calcBaseNames, this.preferenceCalcsBaseNames)) {
            const putParams: LooseObject = {};
            putParams[this.preferenceName] = calcBaseNames;
            this.accountsService.putUserPreferences(putParams).subscribe(() => {
                this.updateState(calcBaseNames);
            });
        }
    }

    createDataColumns(calcInfo: CalcInfo): DataColumnInfo[] {
        const calcBaseNames = Object.keys(calcInfo);
        const calcDataColumns: DataColumnInfo[] = calcBaseNames.map((baseName) => {
            return {
                metricDisplayValue: calcInfo[baseName].label,
                metricInternalName: baseName,
                columnType: 'metric',
            };
        });
        return calcDataColumns;
    }

    abstract updateState(calcNames: string[]): void;
}
